import { GameEvent, OperatorEvent } from "../interfaces/theos.interface";
import { LogLevel, log } from "../utils/log";
import { getWindowParameter, hasWindowParameter } from "../utils/urlParameters";
import { registerEventHandler, triggerEventInGame } from "../feim/registerEventHandler";
import { registerOutBoundMessagesListener, sendGameEvent } from "../feim/sendGameEvent";

/**
 * Handles reality check pauses in a way that won't interrupt gameplay. If a pause is triggered mid-round,
 * the game is only paused after that round ends.
 */

export interface IPausePayload {
    id?: string;
    realityCheck?: boolean;
    autoPlayOnly?: boolean;
}

let roundRunning = false;
let pausePending = false;
let pausePayload: IPausePayload | undefined = undefined;

let startTimeMs = 0;
let initialRcElapsed = 0;

const checkPause = (): void =>
{
    if (!pausePending || roundRunning) {
        return;
    }

    pausePending = false;

    // autoPlayOnly pause events will not reach this module

    triggerEventInGame(OperatorEvent.FREEZE);
    sendGameEvent(GameEvent.PAUSED, pausePayload);
    pausePayload = undefined;
};

const triggerPause = (payload?: IPausePayload): void =>
{
    pausePayload = payload;

    if (pausePending) {
        return;
    }

    pausePending = true;
    checkPause();
};

const triggerResume = (payload?: IPausePayload): void =>
{
    log("Resume triggered with payload", LogLevel.ALL_MESSAGES, payload);
    pausePending = false;
    triggerEventInGame(OperatorEvent.UNFREEZE);
    sendGameEvent(GameEvent.RESUMING_AFTER_PAUSE, payload);
};

/**
 * In some situations reality check is triggered by an internal timer in FEIM, not the operator postMessages.
 * This will create that timer if the environment requires it.
 * This is only used by the Legacy RC.
 */
export const handleClientSideRcInterval = (): void =>
{
    if (!hasWindowParameter("rcinterval"))
    {
        // rcinterval is not defined - RC must be triggered by the operator
        return;
    }

    if (!hasWindowParameter("rciframeurl"))
    {
        // rciframeurl is not defined - RC must be handled by the game
        return;
    }

    const rcinterval = Number(getWindowParameter("rcinterval"));

    if (isNaN(rcinterval) || rcinterval <= 0)
    {
        return;
    }

    if (startTimeMs !== 0) {
        // A timer already exists
        return;
    }

    startTimeMs = Date.now();
    initialRcElapsed = Number(getWindowParameter("rcelapsed") ?? 0);

    const nextRcTriggerSeconds =
        initialRcElapsed === 0
            ? rcinterval
            : rcinterval - initialRcElapsed % rcinterval;

    const ONE_SECOND = 1000;

    const triggerRealityCheckTime = (): void =>
    {
        const totalElapsed = initialRcElapsed + Math.round((Date.now() - startTimeMs) / ONE_SECOND);

        sendGameEvent(GameEvent.REPORT_SESSION_DETAILS, {sessionTime: totalElapsed});
        triggerPause({ realityCheck: true });

        setTimeout(triggerRealityCheckTime, rcinterval * ONE_SECOND);
    };

    setTimeout(triggerRealityCheckTime, nextRcTriggerSeconds * ONE_SECOND);
};

/**
 * @internal
 * Internal handler for reality check or other pause messages.
 * The game will be frozen once a round is no longer in progress, and a paused event is dispatched.
 */
export const handleIdlePause = (): void =>
{
    registerOutBoundMessagesListener((event) =>
    {
        if (event.type === GameEvent.ROUND_STARTED) {
            roundRunning = true;
        }
        else if (event.type === GameEvent.ROUND_FINISHED) {
            roundRunning = false;
            checkPause();
        }
    });

    registerEventHandler(OperatorEvent._PAUSE_WHEN_IDLE)(triggerPause);
    registerEventHandler(OperatorEvent._RESUME)(triggerResume);
};